/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useEffect, useState} from 'react'
import {Box} from '@mui/material'
import DataTable, {PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import FilePreviewDialog from 'src/components-new/feedback/FilePreviewDialog'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import useFilePreview from 'src/components-new/feedback/useFilePreview'
import {docTypesOptions, STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import DocumentInfoDialogNew from 'src/pages-new/documents/DocumentInfoDialogNew'
import {buildMaterialsColumns, MaterialRecord} from 'src/pages-new/infoMaterials/columns'

// Mirror di UsefulMaterialsTab.tsx (src/pages-new/infoMaterials), ma sorgente
// myDocumentsConfigurations.sharedDocuments — piatta, senza il nesting `.materials`
// di infoMaterialsConfigurations.
export default function SharedDocumentsTab(): JSX.Element {
  const sharedDocumentsConfig = useAppSelector(
    (state) => state.pspConfigReducer.data.myDocumentsConfigurations?.sharedDocuments
  )
  const categoryOptions = sharedDocumentsConfig?.categoryOptions ?? []
  // "Nome file" is dropped as its own filter: the "Nome" column/search already covers it
  // (see apiClient.getInfoMaterials), so showing both only confused users into typing the
  // same value into the wrong box when a document's `name` metadata was blank.
  const filtersConfig = (sharedDocumentsConfig?.filtersConfig ?? [])
    .filter((item: FilterItem) => item.id !== 'fileName')
  const columnOptions = sharedDocumentsConfig?.columnOptions

  const [data, setData] = useState<MaterialRecord[]>([])
  const [count, setCount] = useState(0)
  const [loadStatus, setLoadStatus] = useState<string>(STATUS.PENDING)
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    rowsPerPage: 10,
    order: '-createdAt',
  })
  const [activeFilters, setActiveFilters] = useState<FilterResult[]>([])
  const [infoMat, setInfoMat] = useState<MaterialRecord | null>(null)

  const toast = useToast()
  const {viewerState, statusDownload, preview, close} = useFilePreview()

  // ── helpers ────────────────────────────────────────────────────────────────

  const parseCategoryToLabel = (value: string): string | undefined =>
    categoryOptions.find((c: any) => c.value === value)?.label

  const parseCategories = (category?: string[]): string => {
    if (category && category.length > 0) {
      return category.map((c) => parseCategoryToLabel(c) ?? c).join(', ')
    }
    return '-'
  }

  const handleView = (row: MaterialRecord) => {
    if (!row.fileId) return
    preview({
      fileId: row.fileId,
      fileName: row.fileName ?? '',
      downloadName: row.fileName ?? row.name ?? 'Anteprima',
    })
  }

  useEffect(() => {
    if (loadStatus === STATUS.REJECTED) {
      toast.error('Errore nel recuperare i documenti condivisi.')
    }
  }, [loadStatus])

  useEffect(() => {
    if (statusDownload === STATUS.REJECTED) toast.error('Errore nel scaricare il documento.')
  }, [statusDownload])

  // ── data fetching ────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoadStatus(STATUS.PENDING)
    const fetched: any[] = (await apiClient.getInfoMaterials(activeFilters, setLoadStatus, pagination)) ?? []
    const parsed: MaterialRecord[] = fetched.map((mat: any) => ({
      ...mat,
      categoryReadable: parseCategories(mat.category),
      type: docTypesOptions.find((t: any) => t.value === mat.type)?.label ?? mat.type,
    }))
    setData(parsed)
    const cnt = (await apiClient.getInfoMaterialsCount(activeFilters, () => {})) ?? 0
    setCount(Number(cnt))
  }, [activeFilters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // ── handlers ───────────────────────────────────────────────────────────────

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setActiveFilters(values)
    setPagination((p) => ({...p, page: 0}))
  }

  // ── derived ─────────────────────────────────────────────────────────────────

  const columns = buildMaterialsColumns({
    onInfo: (row) => setInfoMat(row),
    onView: handleView,
    columnOptions,
  })
  // Only show the mobile card header when "name" survived the PSP's columnOptions
  // (or no config was set at all) — otherwise a disabled column would still show up there.
  const hasNameColumn = columns.some((col) => col.key === 'name')

  const filterConfig: FilterItem[] = filtersConfig

  // ── render ───────────────────────────────────────────────────────────────────

  return (
    <Box>
      {filterConfig.length > 0 && (
        <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
          <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />
        </Box>
      )}

      <DataTable<MaterialRecord>
        columns={columns}
        rows={data}
        keyField="_id"
        loading={loadStatus === STATUS.PENDING}
        emptyMessage="Nessun documento trovato"
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
        onRowClick={handleView}
        mobileCardTitle={hasNameColumn ? (row) => row.name ?? row.fileName ?? '—' : undefined}
      />

      {infoMat && (
        <DocumentInfoDialogNew
          doc={infoMat as any}
          open={!!infoMat}
          onClose={() => setInfoMat(null)}
          onSave={() => setInfoMat(null)}
          readOnly
          categoryOptions={categoryOptions}
        />
      )}

      <FilePreviewDialog
        fileName={viewerState.fileName}
        filePath={viewerState.filePath}
        mime={viewerState.mime}
        onClose={close}
        open={viewerState.open}
        status={viewerState.status}
      />
    </Box>
  )
}
